<template>
  <div v-if="visible" class="consent-gate">
    <div class="consent-card">
      <div class="consent-head">
        <h1>{{ doc?.title || "Datenschutzerklärung" }}</h1>
        <p class="consent-sub">
          Bevor es weitergeht: Bitte lies die Datenschutzerklärung und stimme der
          Verarbeitung Deiner Daten zu. Ohne Zustimmung ist eine Nutzung nicht möglich.
          <span v-if="doc" class="consent-ver">Fassung {{ doc.version }}</span>
        </p>
      </div>

      <div v-if="loading" class="consent-body consent-state">Datenschutzerklärung wird geladen …</div>
      <div v-else-if="loadError" class="consent-body consent-state consent-error">
        {{ loadError }}
      </div>
      <!-- eslint-disable-next-line vue/no-v-html -- Ausgabe stammt aus legalMarkdownToHtml(),
           das jede Eingabe escaped und nur eigenes Markup erzeugt. -->
      <div v-else class="consent-body" v-html="html"></div>

      <label class="consent-check">
        <input v-model="accepted" type="checkbox" :disabled="loading || !!loadError" />
        <span>{{ checkboxLabel }}</span>
      </label>

      <p v-if="submitError" class="consent-error">{{ submitError }}</p>

      <div class="consent-actions">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!accepted || saving || loading || !!loadError"
          @click="submit"
        >
          <span v-if="saving">Wird gespeichert …</span>
          <span v-else>Zustimmen und fortfahren</span>
        </button>
        <button type="button" class="btn btn-light" :disabled="saving" @click="onLogout">
          Abmelden
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * ConsentGate — blockierende Ebene für ausstehende Datenschutz-Zustimmungen.
 *
 * Warum global in App.vue und nicht auf einer eigenen Route: Die Sperre muss
 * jeden Einstieg abdecken — Deeplink, wiederhergestellte Sitzung, nativer
 * App-Start. Eine Route liesse sich umgehen, indem man direkt woanders landet.
 *
 * Der Gate ist die ZWEITE Verteidigungslinie. Die erste ist die Zustimmung im
 * Registrierungsweg selbst (studio/server/routes/staff.routes.js bzw.
 * clients.routes.js). Der Gate fängt, was dort nicht durchlaufen konnte:
 * Bestandskonten von vor dieser Änderung, Konten aus noch nicht gebauten
 * Registrierungswegen und jede NEUE Fassung des Textes — die macht alle
 * bestehenden Zustimmungen automatisch wieder offen.
 */
import { computed, defineComponent, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { legalMarkdownToHtml } from "@/core/helpers/legalMarkdown";

export default defineComponent({
  name: "consent-gate",
  setup() {
    const authStore = useAuthStore();

    const html = ref("");
    const doc = ref<{ id: string; title: string; version: number } | null>(null);
    const checkboxLabel = ref(
      "Ich habe die Datenschutzerklärung gelesen und willige in die dort beschriebene Verarbeitung meiner personenbezogenen Daten ein."
    );
    const accepted = ref(false);
    const loading = ref(false);
    const loadError = ref("");
    const submitError = ref("");
    const saving = ref(false);

    const pending = computed(() => authStore.pendingConsents || []);
    const visible = computed(() => authStore.isAuthenticated && pending.value.length > 0);

    async function loadCurrent() {
      const next = pending.value[0];
      if (!next) return;
      loading.value = true;
      loadError.value = "";
      try {
        const data = await authStore.loadLegalDocument(next.doc_type, next.lang || "de");
        doc.value = {
          id: data.document.id,
          title: data.document.title,
          version: data.document.version,
        };
        html.value = legalMarkdownToHtml(data.document.body || "");
        if (data.checkbox_label) checkboxLabel.value = data.checkbox_label;
      } catch (e) {
        loadError.value =
          "Die Datenschutzerklärung konnte nicht geladen werden. Bitte lade die Seite neu oder versuche es später erneut.";
        console.error("[consent] Text nicht ladbar:", e);
      } finally {
        loading.value = false;
      }
    }

    // Beim Erscheinen laden und bei jedem Wechsel des offenen Textes neu —
    // stehen zwei Fassungen aus, wird die zweite nach dem Absenden der ersten
    // nachgeladen statt die Ebene fälschlich zu schliessen.
    watch(
      () => (visible.value ? pending.value[0]?.id : ""),
      (id, prev) => {
        if (!id || id === prev) return;
        accepted.value = false;
        submitError.value = "";
        loadCurrent();
      },
      { immediate: true }
    );

    async function submit() {
      if (!doc.value || !accepted.value) return;
      saving.value = true;
      submitError.value = "";
      try {
        await authStore.acceptConsent(doc.value.id);
      } catch (e: any) {
        submitError.value =
          e?.response?.data?.message ||
          "Die Zustimmung konnte nicht gespeichert werden. Bitte versuche es erneut.";
      } finally {
        saving.value = false;
      }
    }

    function onLogout() {
      authStore.logout();
      window.location.href = "/sign-in";
    }

    return {
      visible,
      doc,
      html,
      checkboxLabel,
      accepted,
      loading,
      loadError,
      submitError,
      saving,
      submit,
      onLogout,
    };
  },
});
</script>

<style scoped>
.consent-gate {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(17, 24, 39, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.consent-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.75rem;
  box-sizing: border-box;
}
.consent-head h1 {
  font-size: 1.15rem;
  margin: 0 0 0.35rem;
  color: #111827;
}
.consent-sub {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0 0 0.9rem;
}
.consent-ver {
  display: inline-block;
  margin-left: 0.35rem;
  color: #9ca3af;
}
.consent-body {
  flex: 1 1 auto;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 0.9rem 1.1rem;
  font-size: 0.82rem;
  line-height: 1.55;
  color: #374151;
}
.consent-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 8rem;
}
.consent-error {
  color: #dc2626;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
}
.consent-check {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  margin: 0.9rem 0 0;
  font-size: 0.83rem;
  color: #374151;
  line-height: 1.45;
}
.consent-check input {
  margin-top: 0.2rem;
  flex: 0 0 auto;
}
.consent-actions {
  display: flex;
  gap: 0.6rem;
  margin-top: 1rem;
}
.consent-actions .btn {
  flex: 1 1 auto;
}
</style>

<style>
/* Nicht scoped: das Markup entsteht per v-html und trüge sonst kein
   Scope-Attribut. Deshalb unter .consent-body eingegrenzt. */
.consent-body h2 {
  font-size: 1rem;
  margin: 1.1rem 0 0.4rem;
  color: #111827;
}
.consent-body h3 {
  font-size: 0.9rem;
  margin: 0.9rem 0 0.3rem;
  color: #111827;
}
.consent-body h4 {
  font-size: 0.84rem;
  margin: 0.8rem 0 0.3rem;
  color: #111827;
}
.consent-body p {
  margin: 0.4rem 0;
}
.consent-body ul {
  margin: 0.4rem 0 0.4rem 1.1rem;
  padding: 0;
}
.consent-body li {
  margin: 0.2rem 0;
}
.consent-body blockquote {
  margin: 0.6rem 0;
  padding: 0.5rem 0.7rem;
  border-left: 3px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
}
.consent-body code {
  background: #eef2ff;
  padding: 0 0.25rem;
  border-radius: 3px;
}
.consent-body .tablewrap {
  overflow-x: auto;
}
.consent-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0;
  font-size: 0.78rem;
}
.consent-body th,
.consent-body td {
  border: 1px solid #e5e7eb;
  padding: 0.35rem 0.5rem;
  text-align: left;
  vertical-align: top;
}
.consent-body th {
  background: #f3f4f6;
}
</style>
